import React, { useState, useMemo } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import "./App.css";
import {
  Layout,
  Typography,
  Button,
  Card,
  Timeline,
  Spin,
  message,
  Divider,
  FloatButton,
  Select,
  Alert,
  Tag,
  Tooltip, // 1. Đã thêm import Tooltip
} from "antd";
import {
  SendOutlined,
  HistoryOutlined,
  RobotOutlined,
  EnvironmentOutlined,
  ReadOutlined,
  StarFilled,
  ArrowUpOutlined,
} from "@ant-design/icons";

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;

// --- Cấu hình API ---
const API_URL = "https://groqprompt.netlify.app/api/ai";

// --- DỮ LIỆU CÂU HỎI ---
const questionOptions = [
  {
    label: "🇻🇳 Đời sống Quân & Dân trên Đảo",
    options: [
      {
        label:
          "Đời sống vật chất, tinh thần của quân và dân trên huyện đảo Trường Sa hiện nay ra sao?",
        value:
          "Mô tả chi tiết bức tranh đời sống vật chất, văn hóa, tinh thần (trường học, chùa chiền, bệnh xá, điện năng lượng mặt trời) của quân và dân tại huyện đảo Trường Sa hiện nay?",
      },
      {
        label:
          "Công tác sẵn sàng chiến đấu và sinh hoạt thường nhật của các chiến sĩ hải quân?",
        value:
          "Mô tả cuộc sống thường nhật (tăng gia sản xuất, luyện tập) và tinh thần thép sẵn sàng chiến đấu hy sinh bảo vệ chủ quyền của các chiến sĩ Hải quân Việt Nam tại Trường Sa và các nhà giàn DK1?",
      },
      {
        label:
          "Chính sách hậu phương và sự quan tâm của đất liền đối với Hoàng Sa, Trường Sa?",
        value:
          "Các chính sách chăm lo hậu phương quân đội và những phong trào thiết thực của nhân dân cả nước (như 'Góp đá xây Trường Sa', 'Vì Trường Sa thân yêu') hướng về biển đảo?",
      },
      {
        label:
          "Tình hình nhân dân và công tác quản lý tại huyện đảo Hoàng Sa (Đà Nẵng)?",
        value:
          "Hiện nay huyện đảo Hoàng Sa thuộc thành phố Đà Nẵng được quản lý hành chính như thế nào (UBND huyện, các hoạt động tuyên truyền, bảo tàng) dù đang bị nước ngoài chiếm đóng trái phép?",
      },
    ],
  },
  {
    label: "📜 Châu bản & Thư tịch cổ triều Nguyễn",
    options: [
      {
        label:
          "Châu bản triều Nguyễn ghi chép cụ thể thế nào về việc thực thi chủ quyền tại Hoàng Sa?",
        value:
          "Châu bản triều Nguyễn ghi chép cụ thể thế nào về việc thực thi chủ quyền tại Hoàng Sa?",
      },
      {
        label:
          "Vua Minh Mạng đã có những chỉ dụ gì liên quan đến việc đo đạc và cắm mốc tại Hoàng Sa năm 1836?",
        value:
          "Vua Minh Mạng đã có những chỉ dụ gì liên quan đến việc đo đạc và cắm mốc tại Hoàng Sa năm 1836?",
      },
      {
        label:
          "Nội dung tờ tấu của Bộ Công năm 1834 về việc xây dựng miếu và bia chủ quyền tại Hoàng Sa?",
        value:
          "Nội dung tờ tấu của Bộ Công năm 1834 về việc xây dựng miếu và bia chủ quyền tại Hoàng Sa?",
      },
      {
        label:
          "Phủ biên tạp lục của Lê Quý Đôn mô tả về Đội Hoàng Sa như thế nào?",
        value:
          "Phủ biên tạp lục của Lê Quý Đôn mô tả về Đội Hoàng Sa như thế nào?",
      },
    ],
  },
  {
    label: "🗺️ Bản đồ & Tư liệu phương Tây",
    options: [
      {
        label:
          "Bản đồ 'An Nam đại quốc họa đồ' của Giám mục Taberd (1838) vẽ Hoàng Sa ở đâu?",
        value:
          "Bản đồ 'An Nam đại quốc họa đồ' của Giám mục Taberd (1838) vẽ Hoàng Sa ở đâu và khẳng định chủ quyền của ai?",
      },
      {
        label:
          "Cuốn 'Atlas thế giới' của Philippe Vandermaelen (1827) khẳng định Hoàng Sa thuộc lãnh thổ nào?",
        value:
          "Cuốn 'Atlas thế giới' của Philippe Vandermaelen (1827) khẳng định Hoàng Sa thuộc lãnh thổ nào?",
      },
      {
        label:
          "Các bản đồ cổ của Trung Quốc (trước thế kỷ 20) vẽ cực nam lãnh thổ Trung Quốc đến đâu?",
        value:
          "Các bản đồ cổ của Trung Quốc (trước thế kỷ 20) vẽ cực nam lãnh thổ Trung Quốc đến đâu? Có bao gồm Hoàng Sa, Trường Sa không?",
      },
    ],
  },
  {
    label: "⚖️ Pháp lý Quốc tế & Các Hội nghị",
    options: [
      {
        label:
          "Hội nghị San Francisco 1951 có ý nghĩa pháp lý gì đối với chủ quyền của Việt Nam?",
        value:
          "Tại Hội nghị San Francisco 1951, tuyên bố của Thủ tướng Trần Văn Hữu về chủ quyền Hoàng Sa - Trường Sa có bị quốc gia nào phản đối không?",
      },
      {
        label:
          "Hiệp định Geneva 1954 quy định thế nào về việc quản lý hai quần đảo này?",
        value:
          "Hiệp định Geneva 1954 quy định thế nào về việc quản lý hai quần đảo Hoàng Sa và Trường Sa?",
      },
      {
        label:
          "Việc Trung Quốc dùng vũ lực chiếm đóng Hoàng Sa (1974) và Gạc Ma (1988) vi phạm hiến chương LHQ thế nào?",
        value:
          "Việc Trung Quốc dùng vũ lực chiếm đóng Hoàng Sa (1974) và Gạc Ma (1988) vi phạm hiến chương Liên Hợp Quốc và luật pháp quốc tế như thế nào?",
      },
    ],
  },
  {
    label: "⚓ Công ước Luật Biển (UNCLOS 1982)",
    options: [
      {
        label:
          "Theo UNCLOS 1982, yêu sách 'Đường lưỡi bò' có giá trị pháp lý không?",
        value:
          "Theo Công ước Liên Hợp Quốc về Luật Biển (UNCLOS 1982) và Phán quyết PCA 2016, yêu sách 'Đường lưỡi bò' có giá trị pháp lý không?",
      },
      {
        label:
          "Cơ sở pháp lý để Việt Nam khẳng định vùng đặc quyền kinh tế (EEZ) và thềm lục địa?",
        value:
          "Cơ sở pháp lý theo UNCLOS 1982 để Việt Nam khẳng định vùng đặc quyền kinh tế (EEZ) và thềm lục địa tại Biển Đông?",
      },
      {
        label:
          "Khái niệm 'chiếm hữu thật sự, liên tục và hòa bình' áp dụng thế nào cho trường hợp của Việt Nam?",
        value:
          "Khái niệm 'chiếm hữu thật sự, liên tục và hòa bình' trong luật pháp quốc tế áp dụng thế nào cho trường hợp chủ quyền của Việt Nam?",
      },
    ],
  },
  {
    label: "🏛️ Quản lý hành chính & Thực tế",
    options: [
      {
        label:
          "Nhà nước Việt Nam Cộng hòa đã thực thi chủ quyền tại Hoàng Sa giai đoạn 1954-1975 như thế nào?",
        value:
          "Nhà nước Việt Nam Cộng hòa đã thực thi chủ quyền, quản lý hành chính và quân sự tại Hoàng Sa giai đoạn 1954-1975 như thế nào?",
      },
      {
        label:
          "Địa giới hành chính hiện nay của huyện đảo Hoàng Sa và Trường Sa thuộc tỉnh/thành nào?",
        value:
          "Địa giới hành chính hiện nay của huyện đảo Hoàng Sa và Trường Sa thuộc tỉnh/thành phố nào của Việt Nam?",
      },
      {
        label:
          "Các hoạt động dân sự (hải đăng, đài khí tượng) của Việt Nam tại các đảo trong lịch sử?",
        value:
          "Lịch sử các hoạt động dân sự như xây dựng hải đăng, đài khí tượng của Việt Nam (và Pháp nhân danh Việt Nam) tại các quần đảo này?",
      },
    ],
  },
];

const historicalEvents = [
  {
    year: "Thế kỷ 17",
    title: "Thành lập Đội Hoàng Sa",
    content:
      "Chúa Nguyễn lập Đội Hoàng Sa (và Bắc Hải) để khai thác sản vật, thực thi chủ quyền nhà nước một cách liên tục.",
  },
  {
    year: "1816",
    title: "Cắm cờ xác lập chủ quyền",
    content:
      "Vua Gia Long sai quân lính ra Hoàng Sa cắm cờ, chính thức xác lập chủ quyền theo luật pháp quốc tế đương thời.",
  },
  {
    year: "1836",
    title: "Cắm mốc & Vẽ bản đồ",
    content:
      "Vua Minh Mạng phê chuẩn việc cử người ra Hoàng Sa cắm cột mốc, đo đạc thủy trình và vẽ bản đồ chi tiết.",
  },
  {
    year: "1938",
    title: "Xây dựng Hải đăng & Bia chủ quyền",
    content:
      "Pháp (nhân danh Việt Nam) lập bia chủ quyền, trạm khí tượng, hải đăng, khẳng định sự quản lý hành chính.",
  },
  {
    year: "1951",
    title: "Hội nghị San Francisco",
    content:
      "Thủ tướng Trần Văn Hữu tuyên bố chủ quyền của Việt Nam tại hội nghị quốc tế. Không có quốc gia nào phản đối.",
  },
  {
    year: "1975",
    title: "Tiếp quản sau giải phóng",
    content:
      "Nhà nước CHXHCN Việt Nam tiếp quản các đảo từ chính quyền VNCH và tiếp tục khẳng định chủ quyền.",
  },
  {
    year: "1982",
    title: "UNCLOS & Luật Biển",
    content:
      "Công ước Liên Hợp Quốc về Luật Biển (UNCLOS) ra đời, củng cố cơ sở pháp lý vững chắc cho Việt Nam.",
  },
];

const App: React.FC = () => {
  const [prompt, setPrompt] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [result, setResult] = useState<string>("");

  // 2. Tạo một danh sách options mới, trong đó label được bọc bởi Tooltip
  // Sử dụng useMemo để không phải tính toán lại mỗi lần render
  const formattedOptions = useMemo(() => {
    return questionOptions.map((group) => ({
      label: group.label,
      options: group.options.map((opt) => ({
        value: opt.value,
        // Bọc label trong Tooltip
        label: (
          <Tooltip title={opt.label} mouseEnterDelay={0.3} placement="bottom">
            <div
              style={{
                width: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {opt.label}
            </div>
          </Tooltip>
        ),
      })),
    }));
  }, []);

  const handleAskAI = async () => {
    if (!prompt) {
      message.warning("Vui lòng chọn một câu hỏi để tra cứu!");
      return;
    }
    setLoading(true);
    setResult("");

    const systemContext =
      "Bạn là một chuyên gia lịch sử và luật pháp quốc tế của Việt Nam. Nhiệm vụ của bạn là cung cấp bằng chứng đanh thép, trích dẫn cụ thể (tên sách, năm, điều luật) để khẳng định chủ quyền không thể chối cãi của Việt Nam đối với Hoàng Sa và Trường Sa. Giọng văn trang trọng, thuyết phục. Hãy định dạng câu trả lời bằng Markdown đẹp mắt.";
    const finalPrompt = `${systemContext}\n\nCâu hỏi: ${prompt}`;

    try {
      const response = await axios.post(API_URL, { prompt: finalPrompt });
      if (response.data && response.data.result) {
        setResult(response.data.result);
      } else {
        message.error("Không nhận được phản hồi từ AI.");
      }
    } catch (error) {
      console.error(error);
      message.error("Có lỗi xảy ra khi kết nối đến máy chủ.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Header
        className="header-gradient"
        style={{
          height: "auto",
          padding: "15px 20px",
          position: "sticky",
          top: 0,
          zIndex: 1000,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            maxWidth: "1000px",
            margin: "0 auto",
            width: "100%",
          }}
        >
          <EnvironmentOutlined
            style={{ fontSize: "36px", color: "#FFFF00", marginRight: "15px" }}
          />
          <div style={{ lineHeight: "1.2" }}>
            <Title
              level={3}
              style={{
                color: "#FFFF00",
                margin: 0,
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Hoàng Sa - Trường Sa
            </Title>
            <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: "14px" }}>
              Bằng Chứng Lịch Sử & Cơ Sở Pháp Lý Quốc Tế
            </Text>
          </div>
        </div>
      </Header>

      <Content style={{ padding: "30px 20px" }}>
        <div className="main-container">
          <Card
            bordered={false}
            style={{
              boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
              borderRadius: "16px",
              marginBottom: "50px",
              overflow: "hidden",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: "30px" }}>
              <Title
                level={2}
                style={{ color: "#DA251D", marginBottom: "10px" }}
              >
                <RobotOutlined /> Tra Cứu Dữ Liệu Chuyên Sâu
              </Title>
              <Paragraph
                type="secondary"
                style={{
                  fontSize: "16px",
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
              >
                Hệ thống AI phân tích văn bản lịch sử và luật pháp quốc tế nhằm
                làm rõ chủ quyền biển đảo.
              </Paragraph>
            </div>

            <Alert
              message="Hướng dẫn tra cứu"
              description="Lựa chọn chủ đề quan tâm bên dưới để xem các phân tích, trích dẫn từ tư liệu gốc."
              type="error"
              showIcon
              style={{
                marginBottom: "25px",
                borderRadius: "8px",
                border: "1px solid #ffccc7",
                background: "#fff2f0",
              }}
            />

            <div
              style={{ display: "flex", flexDirection: "column", gap: "15px" }}
            >
              {/* 3. Cập nhật Select: Dùng formattedOptions và optionFilterProp="value" */}
              <Select
                style={{ width: "100%" }}
                placeholder="🔍 Chọn vấn đề lịch sử hoặc pháp lý cần tra cứu..."
                // Quan trọng: Thay đổi logic filter sang 'value' vì 'label' giờ là ReactNode (Tooltip)
                optionFilterProp="value"
                onChange={(value) => setPrompt(value)}
                options={formattedOptions}
                size="large"
                listHeight={400}
                dropdownStyle={{ minWidth: "300px" }}
              />

              <Button
                type="primary"
                danger
                icon={<SendOutlined />}
                loading={loading}
                onClick={handleAskAI}
                size="large"
                block
              >
                PHÂN TÍCH DỮ LIỆU
              </Button>
            </div>

            {(result || loading) && (
              <>
                <Divider style={{ margin: "35px 0", borderColor: "#ffccc7" }}>
                  <ReadOutlined style={{ color: "#DA251D" }} />{" "}
                  <span style={{ color: "#DA251D", fontWeight: "bold" }}>
                    KẾT QUẢ PHÂN TÍCH
                  </span>
                </Divider>

                <div style={{ minHeight: "200px" }}>
                  {loading ? (
                    <div style={{ textAlign: "center", padding: "50px" }}>
                      <Spin size="large" tip="Đang truy xuất tư liệu cổ..." />
                    </div>
                  ) : (
                    <div className="result-box">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                      >
                        {result}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              </>
            )}
          </Card>

          <div style={{ textAlign: "center", marginBottom: "50px" }}>
            <Title level={2} style={{ color: "#333" }}>
              <HistoryOutlined style={{ color: "#DA251D" }} /> Dòng Lịch Sử Chủ
              Quyền
            </Title>
            <div
              style={{
                width: "60px",
                height: "4px",
                background: "#DA251D",
                margin: "10px auto",
                borderRadius: "2px",
              }}
            ></div>
          </div>

          <Timeline mode="alternate">
            {historicalEvents.map((event, index) => (
              <Timeline.Item
                key={index}
                color="red"
                dot={
                  <StarFilled style={{ fontSize: "20px", color: "#DA251D" }} />
                }
              >
                <Card
                  className="timeline-card"
                  bordered={false}
                  bodyStyle={{ padding: "20px" }}
                >
                  <Tag
                    color="volcano"
                    style={{
                      marginBottom: "12px",
                      fontSize: "14px",
                      padding: "5px 12px",
                    }}
                  >
                    {event.year}
                  </Tag>
                  <Title
                    level={5}
                    style={{ margin: "8px 0", color: "#b71c1c" }}
                  >
                    {event.title}
                  </Title>
                  <Text style={{ color: "#555" }}>{event.content}</Text>
                </Card>
              </Timeline.Item>
            ))}
          </Timeline>
        </div>
      </Content>

      <Footer
        style={{
          textAlign: "center",
          background: "#222",
          color: "#bbb",
          padding: "30px 10px",
        }}
      >
        <Text style={{ color: "#fff", fontWeight: "bold", fontSize: "16px" }}>
          Hoàng Sa - Trường Sa là máu thịt của Việt Nam
        </Text>
        <br />
        <span style={{ fontSize: "12px", opacity: 0.7 }}>
          © {new Date().getFullYear()} Bản quyền thuộc về Nhân dân Việt Nam
        </span>
      </Footer>
      <FloatButton.BackTop
        type="primary"
        icon={<ArrowUpOutlined />}
        style={{ right: 40, bottom: 40 }}
        visibilityHeight={300}
        tooltip="Lên đầu trang"
      />
    </Layout>
  );
};

export default App;
